import { useState } from "react";
import { useSiteContent } from "../context/SiteContent";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Button } from "../components/ui/button";
import ProfileDownloadModal from "../components/ProfileDownloadModal";
import SeoHead from "../components/SeoHead";
import { Mail, MapPin, BookOpen, Award, Users, ImageIcon, ExternalLink } from "lucide-react";

const resolveImg = (url) => {
  if (!url) return null;
  return url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

const LinkedName = ({ url, children, className = "" }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`no-underline hover:text-[#B8945A] transition-colors duration-200 ${className}`}>{children}</a>
  ) : (
    <span className={className}>{children}</span>
  );

const CurriculumVitae = () => {
  const { personalInfo, experience, education, publications, certifications, memberships, awards } = useSiteContent();
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name: personalInfo.name,
      jobTitle: personalInfo.title,
      hasOccupation: (experience || []).slice(0, 10).map((e) => ({
        "@type": "Role",
        roleName: e.title,
        name: e.organization,
        startDate: e.period,
        description: e.description,
      })),
      alumniOf: (education || []).map((e) => ({ "@type": "EducationalOrganization", name: e.institution })),
      award: (awards || []).map((a) => a.title),
    },
  };

  return (
    <div>
      <SeoHead
        title="CV | Abhilash Puljal — Cross-Border Strategy & Development"
        description="Curriculum vitae of Abhilash Puljal. LSE-trained advisor with 23+ years across 35+ countries. Clients include EU, UKAID, USAID, AfDB, GIZ, UNHCR and UN WFP."
        path="/cv"
        type="profile"
        jsonLd={jsonLd}
      />

      <section className="py-20 bg-[#FAF8F4]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Professional Record</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1C2B3A] mb-4">Curriculum Vitae</h1>
            <div className="w-20 h-[2px] bg-[#B8945A]"></div>
          </div>
          <Button onClick={() => setProfileModalOpen(true)} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white px-6 py-2.5 rounded font-sans text-sm tracking-wider inline-flex items-center gap-2 w-fit h-auto" data-testid="download-profile-btn">
            <Mail size={16} /> Request Profile by Email
          </Button>
        </div>
      </section>

      <ProfileDownloadModal open={profileModalOpen} onOpenChange={setProfileModalOpen} />

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Tabs defaultValue="experience" className="w-full">
            <TabsList className="bg-[#FAF8F4] border border-[#E5DDD3] p-1 rounded-lg mb-14 w-full md:w-auto flex flex-wrap h-auto" data-testid="cv-tabs">
              {["experience", "education", "credentials", "publications"].map((v) => (
                <TabsTrigger key={v} value={v} className="font-sans text-sm data-[state=active]:bg-white data-[state=active]:text-[#1C2B3A] data-[state=active]:shadow-sm text-[#7A7A7A] rounded px-5 py-2.5 capitalize" data-testid={`cv-tab-${v}`}>{v}</TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="experience">
              <div className="relative">
                <div className="absolute left-[11px] top-2 bottom-2 w-px bg-[#E5DDD3]"></div>
                <div className="space-y-8">
                  {experience.map((exp, i) => (
                    <div key={i} className="relative pl-10">
                      <div className="absolute left-[5px] top-2 w-[13px] h-[13px] rounded-full bg-[#B8945A] border-[3px] border-white shadow-sm"></div>
                      <div className="bg-[#FAF8F4] rounded-lg p-6 border border-[#E5DDD3] hover:border-[#B8945A]/30 transition-colors duration-200">
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                          <h3 className="font-serif text-base font-bold text-[#1C2B3A]">{exp.title}</h3>
                          <span className="font-sans text-xs text-[#7A7A7A] bg-white px-3 py-1 rounded-full border border-[#E5DDD3] whitespace-nowrap">{exp.period}</span>
                        </div>
                        <p className="font-sans text-sm text-[#B8945A] mb-0.5"><LinkedName url={exp.url} className="text-[#B8945A]">{exp.organization}</LinkedName></p>
                        <p className="font-sans text-xs text-[#7A7A7A] flex items-center gap-1 mb-3"><MapPin size={11} />{exp.location}</p>
                        <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">{exp.description}</p>
                        {exp.highlights && exp.highlights.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            {exp.highlights.map((h, j) => <p key={j} className="font-sans text-xs text-[#7A7A7A] pl-3 border-l-2 border-[#B8945A]/30">{h}</p>)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="education">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {education.map((edu, i) => (
                  <Card key={i} className="border-[#E5DDD3] bg-[#FAF8F4]">
                    <CardContent className="p-7">
                      <div className="flex items-start gap-5">
                        <div className="w-16 h-16 rounded-lg border border-[#E5DDD3] flex items-center justify-center flex-shrink-0 bg-white overflow-hidden">
                          {edu.logo_url ? <img src={resolveImg(edu.logo_url)} alt={edu.institution} className="w-full h-full object-contain" /> : <ImageIcon size={20} className="text-[#D5CFC7]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-1">{edu.degree}</h3>
                          <p className="font-sans text-sm text-[#B8945A]"><LinkedName url={edu.url} className="text-[#B8945A]">{edu.institution}</LinkedName></p>
                          <p className="font-sans text-xs text-[#7A7A7A] mt-1">{edu.location} · {edu.period}</p>
                          {edu.note && <p className="font-sans text-sm text-[#4A4A4A] mt-3">{edu.note}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="credentials">
              <div className="space-y-10">
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#1C2B3A] mb-6 flex items-center gap-2"><Award size={20} className="text-[#B8945A]" /> Awards &amp; Recognition</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {awards.map((a, i) => (
                      <div key={i} className="p-5 rounded-lg border border-[#E5DDD3] bg-[#FAF8F4]">
                        <h4 className="font-serif text-base font-bold text-[#1C2B3A] mb-1">{a.title}</h4>
                        <p className="font-sans text-sm text-[#B8945A]">{a.institution}</p>
                        <p className="font-sans text-xs text-[#7A7A7A] mt-0.5">{a.year}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator className="bg-[#E5DDD3]" />
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#1C2B3A] mb-6 flex items-center gap-2"><Award size={20} className="text-[#B8945A]" /> Certifications</h3>
                  <div className="space-y-3">
                    {certifications.map((c, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 rounded-lg border border-[#E5DDD3] bg-[#FAF8F4]">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#B8945A] mt-2 flex-shrink-0"></div>
                        <div><p className="font-sans text-sm font-medium text-[#1C2B3A]">{c.title}</p><p className="font-sans text-xs text-[#7A7A7A]">{c.institution}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator className="bg-[#E5DDD3]" />
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#1C2B3A] mb-6">Languages</h3>
                  <div className="flex flex-wrap gap-4">
                    {[{ language: "Telugu", level: "Mother Tongue" }, { language: "Hindi", level: "Fluent" }, { language: "English", level: "Fluent" }].map((l) => (
                      <div key={l.language} className="px-6 py-4 rounded-lg border border-[#E5DDD3] bg-[#FAF8F4] min-w-[120px]">
                        <p className="font-sans text-sm font-medium text-[#1C2B3A]">{l.language}</p>
                        <p className="font-sans text-xs text-[#7A7A7A] mt-0.5">{l.level}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator className="bg-[#E5DDD3]" />
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#1C2B3A] mb-6 flex items-center gap-2"><Users size={20} className="text-[#B8945A]" /> Professional Memberships</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {memberships.map((m, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 rounded-lg border border-[#E5DDD3] bg-[#FAF8F4]">
                        {m.logo_url ? <img src={resolveImg(m.logo_url)} alt={m.organization} className="w-6 h-6 object-contain flex-shrink-0 mt-0.5" /> : <div className="w-1.5 h-1.5 rounded-full bg-[#B8945A] mt-2 flex-shrink-0"></div>}
                        <div><p className="font-sans text-sm text-[#1C2B3A] font-medium">{m.title}</p><p className="font-sans text-xs text-[#7A7A7A]"><LinkedName url={m.url} className="text-[#7A7A7A]">{m.organization}</LinkedName></p></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="publications">
              <div className="space-y-4">
                {publications.map((pub, i) => (
                  <div key={i} className="flex gap-4 p-5 rounded-lg border border-[#E5DDD3] bg-[#FAF8F4] hover:border-[#B8945A]/40 transition-colors duration-200" data-testid={`publication-${i}`}>
                    <BookOpen size={18} className="text-[#B8945A] flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      {pub.link ? (
                        <a href={pub.link} target="_blank" rel="noopener noreferrer" className="font-serif text-sm font-bold text-[#1C2B3A] leading-snug hover:text-[#B8945A] transition-colors inline-flex items-baseline gap-1.5" data-testid={`publication-link-${i}`}>
                          "{pub.title}" <ExternalLink size={12} className="text-[#B8945A] flex-shrink-0" />
                        </a>
                      ) : (
                        <p className="font-serif text-sm font-bold text-[#1C2B3A] leading-snug">"{pub.title}"</p>
                      )}
                      <p className="font-sans text-xs text-[#7A7A7A] mt-1.5">
                        <span className="italic">{pub.publication}</span>
                        {pub.coAuthors && <span> (with {pub.coAuthors})</span>}
                        {pub.date && <span> · {pub.date}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default CurriculumVitae;
